/**
 * E3/W5:给某一句 b-roll 挑一张产品画面 —— 两个来源页签:
 *
 * 「我的作品」(E3 存量):海报出图资产,两步选(作品 → 画面);上送 assetId。
 * 「产品物料」(W5):双 Pack 产品物料库,两步选(产品 → 物料);上送 packMaterialId。
 *   detail 槽排最前(细节特写就是给 b-roll 插播备的,见槽位说明),其余槽也可选 ——
 *   槽是整理手段不是消费栅栏。STAFF 可见可选:消费跟物料读口径(三角色)走,拍板 2026-07-27。
 *
 * ★两个 id 二选一,由后端解析 objectKey(console 不下发 key,防 SSRF 口径)。
 * ★为什么不做 AI 自动配图:2026-07-23 spike 实测(见 git 版本注释),硬做等于替用户多花出图钱。
 */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { pickableMedia } from "@/entities/media-asset/api/pickableMedia";
import {
  PRODUCT_SLOT_META,
  productMaterialService,
} from "@/entities/product-material/api/productMaterialService";
import { productService } from "@/entities/product/api/productService";
import { Button } from "@/shared/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/shared/ui/dialog";
import { cn } from "@/shared/lib/utils";

export interface BrollMaterial {
  /** 我的作品(海报资产)—— 与 packMaterialId 二选一 */
  assetId?: number;
  /** 产品物料(双 Pack)—— 与 assetId 二选一 */
  packMaterialId?: number;
  thumbUrl: string | null;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPick: (m: BrollMaterial) => void;
}

type SourceTab = "works" | "products";

/** 「我的作品」每页件数(4 列网格 × 6 行,弹框 60vh 内约一屏半)。 */
const WORKS_PAGE_SIZE = 24;

/** detail 排最前:细节特写就是给插播备的;其余槽保物料区顺序。 */
const SLOT_PRIORITY: Record<string, number> = {
  detail: 0,
  angle: 1,
  main: 2,
  package: 3,
  scale: 4,
};

export function BrollMaterialPicker({ open, onOpenChange, onPick }: Props) {
  const [tab, setTab] = useState<SourceTab>("works");
  const [taskId, setTaskId] = useState<number | null>(null);
  const [productCode, setProductCode] = useState<string | null>(null);
  /**
   * 「我的作品」页码(2026-09-09 用户「只有一部分数据」):此前恒拉第 0 页 24 件封顶,
   * 第 25 件起在弹框里凭空消失且没有任何「还有更多」的迹象。改成真分页
   * (上一页/下一页,总页数以接口 total 为准,用户拍板要分页不要加载更多)。
   */
  const [worksPage, setWorksPage] = useState(0);

  const tasksQuery = useQuery({
    queryKey: ["broll", "poster-tasks", worksPage],
    queryFn: () => pickableMedia.listWorks(worksPage, WORKS_PAGE_SIZE),
    // 翻页时旧页数据先顶着,不闪骨架(placeholderData 保留上一份)
    placeholderData: (prev) => prev,
    enabled: open && tab === "works",
  });

  const assetsQuery = useQuery({
    queryKey: ["broll", "poster-assets", taskId],
    queryFn: () => pickableMedia.listImages(taskId as number),
    enabled: open && tab === "works" && taskId !== null,
  });

  const productsQuery = useQuery({
    queryKey: ["broll", "products"],
    queryFn: () => productService.list(),
    enabled: open && tab === "products",
  });

  const materialsQuery = useQuery({
    queryKey: ["broll", "product-materials", productCode],
    queryFn: () => productMaterialService.list(productCode as string),
    enabled: open && tab === "products" && productCode !== null,
  });

  const tasks = tasksQuery.data?.records ?? [];
  const worksTotal = tasksQuery.data?.total ?? 0;
  const worksPageCount = Math.max(1, Math.ceil(worksTotal / WORKS_PAGE_SIZE));
  const assets = (assetsQuery.data ?? []).filter((a) => a.status === "SUCCESS");
  // 只列真产品节点(category/group 是目录不是货),下架的不进消费面
  const products = (productsQuery.data ?? []).filter((p) => p.type === "product" && p.status === 1);
  const materials = [...(materialsQuery.data ?? [])].sort(
    (a, b) => (SLOT_PRIORITY[a.slot] ?? 9) - (SLOT_PRIORITY[b.slot] ?? 9)
  );

  const resetDrill = () => {
    setTaskId(null);
    setProductCode(null);
  };

  const close = () => {
    resetDrill();
    // 下次打开回到第一页(最新作品)—— 弹框是「临时挑一张」,不是要接着上次翻到哪
    setWorksPage(0);
    onOpenChange(false);
  };

  const inDrill = tab === "works" ? taskId !== null : productCode !== null;
  const title =
    tab === "works"
      ? taskId === null
        ? "选一件作品"
        : "选一张画面"
      : productCode === null
        ? "选一个产品"
        : "选一张物料";

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) resetDrill();
        onOpenChange(v);
      }}
    >
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-sm">{title}</DialogTitle>
        </DialogHeader>

        <div className="flex gap-1 border-b border-border">
          {(
            [
              ["works", "我的作品"],
              ["products", "产品物料"],
            ] as const
          ).map(([k, label]) => (
            <button
              key={k}
              type="button"
              onClick={() => {
                setTab(k);
                resetDrill();
              }}
              className={cn(
                "px-3 py-1.5 text-xs transition-colors",
                tab === k
                  ? "border-b-2 border-primary font-medium text-foreground"
                  : "text-muted-foreground hover:text-foreground"
              )}
              data-testid={`broll-tab-${k}`}
            >
              {label}
            </button>
          ))}
        </div>

        {inDrill && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="w-fit text-xs"
            onClick={resetDrill}
          >
            {tab === "works" ? "← 换一件作品" : "← 换一个产品"}
          </Button>
        )}

        <div className="max-h-[60vh] overflow-y-auto">
          {tab === "works" ? (
            taskId === null ? (
              tasksQuery.isLoading ? (
                <p className="p-4 text-xs text-muted-foreground">正在读取你的作品…</p>
              ) : tasks.length === 0 ? (
                // 空态说人话 + 给出口,别让用户对着空白框猜
                <p className="p-4 text-xs text-muted-foreground">
                  还没有可用的出图作品。先去 AI
                  海报出一张图,回来就能给这句配画面;或切「产品物料」直接用产品图。
                </p>
              ) : (
                <>
                  <div className="grid grid-cols-4 gap-2">
                    {tasks.map((t) => (
                      <button
                        key={t.id}
                        type="button"
                        onClick={() => setTaskId(t.id)}
                        className="group overflow-hidden rounded-md border border-border text-left hover:border-primary"
                        data-testid={`broll-task-${t.id}`}
                      >
                        {t.coverThumbUrl ? (
                          <img
                            src={t.coverThumbUrl}
                            alt={t.name}
                            className="aspect-square w-full object-cover"
                            loading="lazy"
                          />
                        ) : (
                          <div className="flex aspect-square w-full items-center justify-center bg-muted text-2xs text-muted-foreground">
                            无预览
                          </div>
                        )}
                        <span className="block truncate px-1.5 py-1 text-2xs text-muted-foreground">
                          {t.name}
                        </span>
                      </button>
                    ))}
                  </div>
                  {worksPageCount > 1 && (
                    <div
                      className="mt-2 flex items-center justify-center gap-3 text-xs text-muted-foreground"
                      data-testid="broll-works-pager"
                    >
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-7 px-2 text-xs"
                        disabled={worksPage === 0 || tasksQuery.isFetching}
                        onClick={() => setWorksPage((n) => Math.max(0, n - 1))}
                      >
                        上一页
                      </Button>
                      <span>
                        {worksPage + 1} / {worksPageCount} 页 · 共 {worksTotal} 件
                      </span>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-7 px-2 text-xs"
                        disabled={worksPage + 1 >= worksPageCount || tasksQuery.isFetching}
                        onClick={() => setWorksPage((n) => n + 1)}
                      >
                        下一页
                      </Button>
                    </div>
                  )}
                </>
              )
            ) : assetsQuery.isLoading ? (
              <p className="p-4 text-xs text-muted-foreground">正在读取这件作品的画面…</p>
            ) : assets.length === 0 ? (
              <p className="p-4 text-xs text-muted-foreground">这件作品没有可用的成功画面。</p>
            ) : (
              <div className="grid grid-cols-4 gap-2">
                {assets.map((a) => (
                  <button
                    key={a.id}
                    type="button"
                    onClick={() => {
                      onPick({ assetId: a.id, thumbUrl: a.thumbUrl ?? a.baseUrl ?? null });
                      close();
                    }}
                    className="overflow-hidden rounded-md border border-border hover:border-primary"
                    data-testid={`broll-asset-${a.id}`}
                  >
                    <img
                      src={a.thumbUrl ?? a.baseUrl ?? ""}
                      alt={`画面 ${a.seq}`}
                      className="aspect-square w-full object-cover"
                      loading="lazy"
                    />
                  </button>
                ))}
              </div>
            )
          ) : productCode === null ? (
            productsQuery.isLoading ? (
              <p className="p-4 text-xs text-muted-foreground">正在读取产品…</p>
            ) : products.length === 0 ? (
              <p className="p-4 text-xs text-muted-foreground">
                还没有上架产品。产品与物料在「产品库」维护,传好图回来就能选。
              </p>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                {products.map((p) => (
                  <button
                    key={p.code}
                    type="button"
                    onClick={() => setProductCode(p.code)}
                    className="rounded-md border border-border px-3 py-2 text-left text-xs hover:border-primary"
                    data-testid={`broll-product-${p.code}`}
                  >
                    <span className="block truncate font-medium">{p.name}</span>
                    {p.summary && (
                      <span className="block truncate text-2xs text-muted-foreground">
                        {p.summary}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )
          ) : materialsQuery.isLoading ? (
            <p className="p-4 text-xs text-muted-foreground">正在读取产品物料…</p>
          ) : materials.length === 0 ? (
            <p className="p-4 text-xs text-muted-foreground">
              这个产品还没传物料。去「产品库 → 物料」传细节/角度图,回来就能给这句配画面。
            </p>
          ) : (
            <div className="grid grid-cols-4 gap-2">
              {materials.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => {
                    onPick({ packMaterialId: m.id, thumbUrl: m.thumbUrl ?? m.url ?? null });
                    close();
                  }}
                  className="overflow-hidden rounded-md border border-border text-left hover:border-primary"
                  data-testid={`broll-material-${m.id}`}
                >
                  {(m.thumbUrl ?? m.url) ? (
                    <img
                      src={m.thumbUrl ?? m.url ?? undefined}
                      alt={m.label ?? PRODUCT_SLOT_META[m.slot].label}
                      className="aspect-square w-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <div className="flex aspect-square w-full items-center justify-center bg-muted text-2xs text-muted-foreground">
                      无预览
                    </div>
                  )}
                  <span className="block truncate px-1.5 py-1 text-2xs text-muted-foreground">
                    {PRODUCT_SLOT_META[m.slot].label}
                    {m.label ? ` · ${m.label}` : ""}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
